
import { Alert, assignLoadable, createLoadingHook, runWithMinimumDelay, type Component, type Loadable } from "vue-utils"
import { usePopups } from "./usePopups"
import { LoadingError } from "../api"
import FullScreenLoading from "../components/FullScreenLoading"

export async function showErrorMessage(title: string, message: string) {
	await usePopups().showAlertPopup(() => (
		<Alert title={title}>
			<p>{message}</p>
		</Alert>
	))
}

export async function showError(error: Error) {
	const popups = usePopups()
	if (error instanceof LoadingError) {
		return await popups.showMessage(error.name, error.message)
	} else {
		return await popups.showMessage("Unexpected Error", error.message)
	}
}

const { LoadingWrapper, useLoading: useLoadingInternal } = createLoadingHook({
	loadingComponent: FullScreenLoading as Component,
	showError: showErrorMessage,
	coolInTimeMs: 100,
	defaultError(error) {
		if (error instanceof LoadingError) {
			return error.message
		}
		return "An unexpected error occurred. Please contact Freddie"
	},
	defaultErrorTitle(error) {
		if (error instanceof LoadingError) {
			return error.name
		}
		return "Unexpected Error"
	},
})

export function useLoading() {
	const loadingInternal = useLoadingInternal()

	async function refreshLoadable<T>(loadable: Loadable<T>): Promise<T> {
		assignLoadable(loadable, {type : "loading"})
		const newValue = await loadingInternal.runAction(Promise.resolve(loadable.queryData()))
		assignLoadable(loadable, { type: "done", result: newValue })
		return newValue
	}

	async function runActionWithMinimumDelay<T>(action: Promise<T>, delay = 250) {
		return await loadingInternal.runAction(runWithMinimumDelay(action, delay))
	}

	return {
		...loadingInternal,
		refreshLoadable,
		runActionWithMinimumDelay,
	}
}

export { LoadingError, LoadingWrapper }
